// Default search filters
export const DEFAULT_SEARCH_FILTERS = {
  location_query: "",
  radius_miles: 20, // Match backend default
  business_type: undefined,
  min_rating: undefined,
  has_availability: undefined, // Changed to undefined to allow "any availability"
  age_groups_served: undefined, // Array of age group strings
  required_core_services: undefined, // Required core services (replaces patient_needs)
  nice_to_have_core_services: undefined, // Nice to have core services (replaces nice_to_have_skills)
  disability_conditions: undefined, // Array of disability conditions
  accessibility_features: undefined, // Array of accessibility features
  funding_accepted: undefined, // Array of funding options
  accepts_medicaid: undefined, // Changed to undefined to allow "any insurance"
  accepts_medicare: undefined, // Changed to undefined to allow "any insurance"
  accepts_private_insurance: undefined, // Changed to undefined to allow "any insurance"
  // Note: verified_only removed from defaults to show all providers by default
  // Users can opt-in to filtering verified only via UI controls
  allow_cross_state: false,
  languages: undefined, // Changed to undefined to allow "any language"
  other_capacity: undefined,
  // sort_by / sort_order omitted — server applies automatic sort contract when unset
  // ✅ Coordinate optimization fields
  lat: undefined,
  lng: undefined,
  coordinates: undefined,
  location_type: undefined,
  location_source: undefined,
  address: undefined,
};

// Pagination settings
export const PAGINATION_CONFIG = {
  /** Desktop list batch size (lg breakpoint and up). Dense cards: prefer 12 over 20. */
  DESKTOP_ITEMS_PER_PAGE: 12,
  /** Mobile/tablet first batch — shorter than desktop for faster scan. */
  MOBILE_ITEMS_PER_PAGE: 10,
  /** @deprecated Prefer DESKTOP_ITEMS_PER_PAGE / getProviderListItemsPerPage(). */
  DEFAULT_ITEMS_PER_PAGE: 12,
  MAX_COMPARE_COUNT: 4,
} as const;

export function getProviderListItemsPerPage(isLargeScreen: boolean): number {
  return isLargeScreen
    ? PAGINATION_CONFIG.DESKTOP_ITEMS_PER_PAGE
    : PAGINATION_CONFIG.MOBILE_ITEMS_PER_PAGE;
}

// Token storage keys (DEPRECATED: Tokens should use httpOnly cookies, not localStorage)
// These are kept for backward compatibility and migration purposes
export const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  TOKEN_EXPIRY: "token_expiry",
  // Legacy keys for migration
  TOKEN: "token",
  AUTH_TOKEN: "authToken",
} as const;

// localStorage keys for application data
export const STORAGE_KEYS = {
  // Provider search related
  SEARCH_RESULTS: "providerSearchResults",
  SEARCH_CENTER: "providerSearchCenter",
  SEARCH_SESSION_METADATA: "providerSearchSessionMetadata",
  LAST_SEARCH_TIME: "providerLastSearchTime",
  /** Filters snapshot from the last successful search (for stale-results detection). */
  LAST_SEARCH_FILTERS: "providerLastSearchFilters",
  /** Search mode used for the last successful search. */
  LAST_SEARCH_MODE: "providerLastSearchMode",
  SEARCH_HISTORY: "providerSearchHistory",

  // Provider favorites and comparison (now managed by Zustand store)
  SAVED_PROVIDERS: "savedProviders",
  COMPARED_PROVIDERS: "comparedProviders",

  // Feature flags
  FEATURE_FLAGS_CACHE: "feature-flags-cache",

  // Auth store (Zustand persist)
  AUTH_STORAGE: "auth-storage",

  // Provider store (Zustand persist)
  PROVIDER_STORE: "provider-store",

  // Form persistence
  LOGIN_FORM: "loginForm",
  SEARCH_HISTORY_DEFAULT: "search_history",
  CONSUMER_REGISTRATION_DRAFT: "consumerRegistrationDraft",
  PROVIDER_REGISTRATION_ACCOUNT_DRAFT: "providerRegistrationAccountDraft",
  CONTEXTUAL_HELP_RETURN: "contextualHelpReturn",
  PROVIDER_SEARCH_SCROLL: "providerSearchScroll",
  /** Session flag: user dismissed AI recommendations promo on provider search form. */
  PROVIDER_SEARCH_AI_PROMO_DISMISSED: "providerSearchAiPromoDismissed",
  /**
   * Session flag: Care services accordion was auto-expanded once when More
   * filters first opened (density polish P2).
   */
  PROVIDER_SEARCH_CARE_ACCORDION_AUTO_EXPANDED:
    "providerSearchCareAccordionAutoExpanded",
  REGISTRATION_LEGAL_RETURN: "registrationLegalReturn",

  // Patient data (offline storage)
  PATIENT_VITAL_SIGNS: "patient_vital_signs",
  PATIENT_MEDICATIONS: "patient_medications",
  PATIENT_ALLERGIES: "patient_allergies",
  PATIENT_CARE_PLANS: "patient_care_plans",
  PATIENT_APPOINTMENTS: "patient_appointments",
  PATIENT_PROVIDERS: "patient_providers",
  PATIENT_HEALTH_RECORDS: "patient_health_records",
  PATIENT_PENDING_CHANGES: "patient_pending_changes",
  PATIENT_LAST_SYNC: "patient_last_sync",
  PATIENT_OFFLINE_MODE: "patient_offline_mode",
} as const;

// Cache settings
export const CACHE_CONFIG = {
  SEARCH_RESULTS_TTL: 60 * 60 * 1000, // 1 hour in milliseconds
  MAX_PROVIDERS_IMPORT: 1000,
  MAX_COMPARED_PROVIDERS_IMPORT: 100,
};

// Storage size limits (in bytes)
export const STORAGE_LIMITS = {
  // Individual item limits
  MAX_ITEM_SIZE: 1 * 1024 * 1024, // 1MB per item
  // Total localStorage quota (typically 5-10MB, we'll use 8MB as safe limit)
  MAX_TOTAL_SIZE: 8 * 1024 * 1024, // 8MB
  // Warning threshold (80% of max)
  WARNING_THRESHOLD: 6.4 * 1024 * 1024, // 6.4MB
  // Limits for specific data types
  MAX_SEARCH_HISTORY_ITEMS: 50, // Maximum search history items
  MAX_SAVED_PROVIDERS: 500, // Maximum saved providers
  MAX_COMPARED_PROVIDERS: 10, // Maximum compared providers
} as const;

// File validation settings
export const FILE_VALIDATION = {
  MAX_FILE_SIZE_MB: 10,
  ALLOWED_TYPES: ["application/json", "text/plain"],
};

/**
 * Registration flow timing constants
 *
 * These constants centralize all timing-related values used in the registration flow
 * to improve maintainability and make it easy to adjust timing behavior.
 *
 */
export const REGISTRATION_TIMING = {
  /**
   * Delay before redirecting to login after successful registration (milliseconds)
   *
   * This delay allows users to see the success message before being redirected.
   * 1500ms provides a good balance between user experience and perceived performance.
   */
  REDIRECT_DELAY: 1500,

  /**
   * Countdown seconds shown on the post-registration success interstitial.
   * 3 seconds gives users time to read the message before redirect.
   */
  COUNTDOWN_SECONDS: 3,

  /**
   * Delay before auto-redirect from the success interstitial (milliseconds).
   * Derived from COUNTDOWN_SECONDS for a single source of truth.
   */
  INTERSTITIAL_DELAY_MS: 3 * 1000,

  /**
   * Debounce delay for real-time validation (milliseconds)
   *
   * Prevents validation from running on every keystroke, improving performance
   * and reducing unnecessary validation calls. 300ms is optimal for most users.
   */
  VALIDATION_DEBOUNCE_MS: 300,

  /** Debounce delay for session draft persistence (milliseconds). */
  DRAFT_SAVE_DEBOUNCE_MS: 300,
} as const;

/** Must match backend `AUTH_ERROR_CODE_EMAIL_NOT_VERIFIED` (login blocked until email verified). */
export const AUTH_ERROR_CODE_EMAIL_NOT_VERIFIED = "EMAIL_NOT_VERIFIED";
